import { FormEvent, useEffect, useMemo, useState } from 'react';
import AdminLayout from '../AdminLayout';
import {
  createChatbotEntry,
  deleteChatbotEntry,
  fetchAdminChatbotEntries,
  reorderChatbotEntries,
  updateChatbotEntry,
} from '../adminService';
import { useAdminSession } from '../adminSession';
import { ApiError } from '../../../services/apiClient';
import {
  AdminChatbotEntry,
  ChatbotEntryInput,
  ChatbotLink,
  invalidateChatbotEntries,
} from '../../../components/chatbot/chatbotService';
import { rankEntries } from '../../../components/chatbot/chatbotEngine';
import formStyles from '../Portfolio/AdminPortfolioForm.module.scss';
import listStyles from '../Portfolio/AdminPortfolio.module.scss';
import styles from './AdminChatbot.module.scss';

const MAX_LINKS = 5;

const EMPTY_FORM: ChatbotEntryInput = {
  question: '',
  answer: '',
  keywords: '',
  links: [],
  quickLabel: '',
  isPublished: true,
};

function AdminChatbot() {
  const [session, setSession] = useAdminSession();
  const [entries, setEntries] = useState<AdminChatbotEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  // null: 편집 안 함, 'new': 새 답변, 숫자: 해당 id 수정
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<ChatbotEntryInput>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [testQuery, setTestQuery] = useState('');

  const handleError = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.status === 401) {
      setSession(null);
    } else {
      setError(err instanceof Error ? err.message : fallback);
    }
  };

  const load = () => {
    if (!session) return;
    fetchAdminChatbotEntries(session.token)
      .then(setEntries)
      .catch((err) => handleError(err, '챗봇 답변을 불러오지 못했습니다.'));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [session]);

  // 사용자 챗봇과 같은 방식으로 공개된 답변만 매칭해 본다
  const testResult = useMemo(() => {
    if (!entries || !testQuery.trim()) return null;
    return rankEntries(
      testQuery,
      entries.filter((entry) => entry.isPublished),
    );
  }, [entries, testQuery]);

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    setIsBusy(true);
    try {
      await action();
      invalidateChatbotEntries();
    } catch (err) {
      handleError(err, '처리 중 오류가 발생했습니다.');
    } finally {
      setIsBusy(false);
    }
  };

  const startEdit = (entry: AdminChatbotEntry | null) => {
    setFormError(null);
    if (entry) {
      setEditing(entry.id);
      setForm({
        question: entry.question,
        answer: entry.answer,
        keywords: entry.keywords,
        links: entry.links.map((link) => ({ ...link })),
        quickLabel: entry.quickLabel ?? '',
        isPublished: entry.isPublished,
      });
    } else {
      setEditing('new');
      setForm(EMPTY_FORM);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setField = <K extends keyof ChatbotEntryInput>(key: K, value: ChatbotEntryInput[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setLink = (index: number, key: keyof ChatbotLink, value: string) =>
    setField(
      'links',
      form.links.map((link, i) => (i === index ? { ...link, [key]: value } : link)),
    );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!session || editing === null) return;
    if (!form.question.trim() || !form.answer.trim()) {
      setFormError('질문과 답변을 입력해주세요.');
      return;
    }

    setFormError(null);
    setIsBusy(true);
    try {
      const saved =
        editing === 'new'
          ? await createChatbotEntry(session.token, form)
          : await updateChatbotEntry(session.token, editing, form);
      invalidateChatbotEntries();
      setEntries((prev) =>
        editing === 'new'
          ? [...(prev ?? []), saved]
          : (prev ?? []).map((entry) => (entry.id === saved.id ? saved : entry)),
      );
      setEditing(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setSession(null);
      } else {
        setFormError(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.');
      }
    } finally {
      setIsBusy(false);
    }
  };

  const handleMove = (index: number, offset: -1 | 1) => {
    if (!session || !entries) return;
    const ids = entries.map((entry) => entry.id);
    const target = index + offset;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    run(async () => setEntries(await reorderChatbotEntries(session.token, ids)));
  };

  const handleDelete = (entry: AdminChatbotEntry) => {
    if (!session) return;
    if (!window.confirm(`"${entry.question}" 답변을 삭제하시겠습니까? 되돌릴 수 없습니다.`)) return;
    run(async () => {
      await deleteChatbotEntry(session.token, entry.id);
      setEntries((prev) => (prev ?? []).filter((e) => e.id !== entry.id));
      if (editing === entry.id) setEditing(null);
    });
  };

  const quickCount = entries?.filter((entry) => entry.quickLabel && entry.isPublished).length ?? 0;

  return (
    <AdminLayout title="챗봇 관리">
      {editing !== null && (
        <div className="section">
          <div className="sec-head">
            <div className="sec-title">{editing === 'new' ? '새 답변 등록' : '답변 수정'}</div>
            <div className="sec-actions">
              <button type="button" className="btn btn-outline" onClick={() => setEditing(null)}>
                취소
              </button>
            </div>
          </div>

          <form className={formStyles.form} onSubmit={handleSubmit}>
            <div className="card">
              <div className={formStyles.grid}>
                <div className={`${formStyles.field} ${formStyles.full}`}>
                  <label>
                    질문<span className={formStyles.required}>*</span>
                  </label>
                  <input
                    value={form.question}
                    maxLength={200}
                    onChange={(e) => setField('question', e.target.value)}
                    placeholder="예: 카드로 결제할 수 있나요?"
                  />
                  <p className={styles.hint}>답변 위에 제목으로, 관련 질문 목록에 버튼으로 표시됩니다.</p>
                </div>
                <div className={`${formStyles.field} ${formStyles.full}`}>
                  <label>
                    답변<span className={formStyles.required}>*</span>
                  </label>
                  <textarea
                    rows={6}
                    value={form.answer}
                    maxLength={2000}
                    onChange={(e) => setField('answer', e.target.value)}
                    placeholder="줄바꿈은 그대로 표시됩니다. 목록은 '• ' 로 시작하면 보기 좋습니다."
                  />
                  <p className={styles.hint}>{form.answer.length} / 2000자</p>
                </div>
                <div className={`${formStyles.field} ${formStyles.full}`}>
                  <label>키워드</label>
                  <input
                    value={form.keywords}
                    maxLength={500}
                    onChange={(e) => setField('keywords', e.target.value)}
                    placeholder="예: 카드 결제 세금계산서 토스페이먼츠"
                  />
                  <p className={styles.hint}>
                    띄어쓰기나 쉼표로 구분합니다. 방문자가 입력한 문장에 키워드가 들어 있으면 이 답변이 나옵니다. (2글자
                    이상, 띄어쓰기 무시 — "시안확인"은 "시안 확인"과도 일치)
                  </p>
                </div>
                <div className={formStyles.field}>
                  <label>빠른 메뉴 버튼 이름</label>
                  <input
                    value={form.quickLabel ?? ''}
                    maxLength={30}
                    onChange={(e) => setField('quickLabel', e.target.value)}
                    placeholder="예: 결제 · 세금계산서"
                  />
                  <p className={styles.hint}>입력하면 챗봇 하단 빠른 메뉴에 버튼으로 나옵니다. 비우면 표시 안 함.</p>
                </div>
                <div className={formStyles.field}>
                  <label className={formStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={form.isPublished}
                      onChange={(e) => setField('isPublished', e.target.checked)}
                    />
                    챗봇에 공개
                  </label>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-title" style={{ marginBottom: 12 }}>
                링크 버튼 (최대 {MAX_LINKS}개)
              </div>
              <p className={styles.hint} style={{ marginBottom: 12 }}>
                주소 형식: 사이트 페이지 <code>/quote</code>, 전화 <code>tel:0532164126</code>, 메일{' '}
                <code>mailto:gn-co@daum.net</code>, 외부 링크 <code>https://...</code>
              </p>
              {form.links.map((link, index) => (
                <div key={index} className={styles.linkRow}>
                  <input
                    value={link.label}
                    maxLength={40}
                    onChange={(e) => setLink(index, 'label', e.target.value)}
                    placeholder="버튼 이름 (예: 견적문의 작성하기)"
                  />
                  <input
                    value={link.url}
                    maxLength={500}
                    onChange={(e) => setLink(index, 'url', e.target.value)}
                    placeholder="/quote"
                  />
                  <button
                    type="button"
                    className={`btn btn-outline ${listStyles.dangerBtn}`}
                    onClick={() =>
                      setField(
                        'links',
                        form.links.filter((_, i) => i !== index),
                      )
                    }
                  >
                    삭제
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline"
                disabled={form.links.length >= MAX_LINKS}
                onClick={() => setField('links', [...form.links, { label: '', url: '' }])}
              >
                + 링크 버튼 추가
              </button>
            </div>

            {formError && (
              <div className="card" style={{ color: '#B91C1C' }}>
                {formError}
              </div>
            )}

            <div className={formStyles.submitRow}>
              <button type="submit" className="btn btn-primary" disabled={isBusy}>
                {isBusy ? '저장 중...' : editing === 'new' ? '등록하기' : '저장하기'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="section">
        <div className="sec-head">
          <div className="sec-title">챗봇 질문 · 답변</div>
          <div className="sec-actions">
            <span className="chip active">전체 {entries?.length ?? 0}건</span>
            <span className="chip">빠른 메뉴 {quickCount}개</span>
            <button type="button" className="btn btn-primary" onClick={() => startEdit(null)}>
              + 새 답변 등록
            </button>
          </div>
        </div>

        {error && (
          <div className="card" style={{ color: '#B91C1C', marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>순서</th>
                  <th>질문</th>
                  <th>빠른 메뉴</th>
                  <th>키워드</th>
                  <th>링크</th>
                  <th>공개</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {!entries && (
                  <tr>
                    <td colSpan={7} className="emptyRow">
                      불러오는 중...
                    </td>
                  </tr>
                )}
                {entries && entries.length === 0 && (
                  <tr>
                    <td colSpan={7} className="emptyRow">
                      등록된 답변이 없습니다.
                    </td>
                  </tr>
                )}
                {entries?.map((entry, index) => (
                  <tr key={entry.id} onClick={() => startEdit(entry)}>
                    <td onClick={(event) => event.stopPropagation()}>
                      <div className={listStyles.rowActions}>
                        <button
                          type="button"
                          className="btn btn-outline"
                          aria-label="위로"
                          disabled={isBusy || index === 0}
                          onClick={() => handleMove(index, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline"
                          aria-label="아래로"
                          disabled={isBusy || index === entries.length - 1}
                          onClick={() => handleMove(index, 1)}
                        >
                          ↓
                        </button>
                      </div>
                    </td>
                    <td className={styles.questionCell}>{entry.question}</td>
                    <td>{entry.quickLabel ? <span className="chip">{entry.quickLabel}</span> : '-'}</td>
                    <td className={styles.keywordCell} title={entry.keywords}>
                      {entry.keywords || <span className={styles.warn}>키워드 없음</span>}
                    </td>
                    <td>{entry.links.length}개</td>
                    <td>
                      <span className={`badgePill ${entry.isPublished ? 'ok' : 'neutral'}`}>
                        <span className="pulse"></span> {entry.isPublished ? '공개' : '비공개'}
                      </span>
                    </td>
                    <td onClick={(event) => event.stopPropagation()}>
                      <div className={listStyles.rowActions}>
                        <button type="button" className="btn btn-outline" onClick={() => startEdit(entry)}>
                          수정
                        </button>
                        <button
                          type="button"
                          className={`btn btn-outline ${listStyles.dangerBtn}`}
                          disabled={isBusy}
                          onClick={() => handleDelete(entry)}
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className={styles.hint} style={{ marginTop: 10 }}>
          순서는 빠른 메뉴 버튼 순서이자, 여러 답변이 같은 점수로 일치할 때의 우선순위입니다.
        </p>
      </div>

      <div className="section">
        <div className="sec-head">
          <div className="sec-title">답변 테스트</div>
        </div>
        <div className="card">
          <div className={formStyles.field}>
            <label>방문자가 이렇게 물어보면</label>
            <input
              value={testQuery}
              onChange={(e) => setTestQuery(e.target.value)}
              placeholder="예: 카드결제 되나요?"
            />
          </div>
          {testResult && (
            <div className={styles.testResult}>
              {testResult.length === 0 ? (
                <p className={styles.warn}>
                  일치하는 답변이 없어 기본 안내(견적문의 · 자주묻는질문 · 전화)가 나갑니다. 키워드를 추가해 보세요.
                </p>
              ) : (
                <>
                  <p>
                    <strong>답변:</strong> {testResult[0].question}
                  </p>
                  {testResult.length > 1 && (
                    <p>
                      <strong>관련 질문:</strong> {testResult.slice(1).map((entry) => entry.question).join(' / ')}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminChatbot;
